import React, { useState } from "react";
import { Sparkles, Flame, Trophy, Hand, BarChart3, Lightbulb, Target } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { useTranslation } from "react-i18next";
import { useCompanion } from "../../context/CompanionProvider";
import type { InsightType, ProactiveInsight } from "../../api/proactiveInsights";

// ─── Insight Type Config ─────────────────────────────────────────────────────

const INSIGHT_EMOJI: Record<InsightType, string> = {
  streak_milestone: "\uD83D\uDD25",
  completion_milestone: "\uD83C\uDFC6",
  idle_nudge: "\uD83D\uDC4B",
  progress_summary: "\uD83D\uDCCA",
  pattern_detected: "\uD83D\uDCA1",
  outcome_tracking: "\uD83C\uDFAF",
  weekly_summary: "\uD83D\uDCC5",
  monthly_summary: "\uD83D\uDCC6",
};

function timeAgo(timestamp: number, t: (key: string, opts?: string | Record<string, unknown>) => string): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return t("time_just_now", "just now");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t("time_minutes_ago", { count: minutes, defaultValue: `${minutes}m ago` });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("time_hours_ago", { count: hours, defaultValue: `${hours}h ago` });
  const days = Math.floor(hours / 24);
  return t("time_days_ago", { count: days, defaultValue: `${days}d ago` });
}

// ─── Component ───────────────────────────────────────────────────────────────

export const InsightIndicator: React.FC = () => {
  const { t } = useTranslation();
  const {
    proactiveInsights,
    unreadInsightCount,
    markInsightSeen,
    dismissInsight,
  } = useCompanion();

  const [isOpen, setIsOpen] = useState(false);

  // Only show when there are unread insights
  if (unreadInsightCount === 0 && proactiveInsights.length === 0) {
    return null;
  }

  const recentInsights = proactiveInsights.slice(0, 5);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={`${t("insights", "Insights")}${
            unreadInsightCount > 0 ? ` (${unreadInsightCount} ${t("unread", "unread")})` : ""
          }`}
        >
          <Sparkles className="w-5 h-5" />
          {unreadInsightCount > 0 && (
            <Badge
              className="absolute -top-1 -end-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-xs font-semibold bg-ai text-ai-foreground animate-pulse"
            >
              {unreadInsightCount > 99 ? "99+" : unreadInsightCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-2rem)] md:w-80 p-0"
        align="end"
        side="bottom"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-ai" />
            <span className="text-sm font-semibold">
              {t("insights") || "Insights"}
            </span>
          </div>
          {unreadInsightCount > 0 && (
            <span className="text-xs text-ai font-medium">
              {unreadInsightCount} {t("new") || "new"}
            </span>
          )}
        </div>

        {/* Insight list */}
        <div className="max-h-80 overflow-y-auto">
          {recentInsights.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Sparkles className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">
                {t("no_insights_yet") || "No insights yet"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t("keep_engaging_insights", "Keep engaging and insights will appear here")}
              </p>
            </div>
          ) : (
            recentInsights.map((insight) => (
              <InsightItem
                key={insight._id}
                insight={insight}
                onRead={() => markInsightSeen(insight._id)}
                onDismiss={() => dismissInsight(insight._id)}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ─── Insight Item ────────────────────────────────────────────────────────────

interface InsightItemProps {
  insight: ProactiveInsight;
  onRead: () => void;
  onDismiss: () => void;
}

const InsightItem: React.FC<InsightItemProps> = ({ insight, onRead }) => {
  const { t } = useTranslation();
  const isUnread =
    insight.status === "generated" || insight.status === "delivered";
  const emoji =
    INSIGHT_EMOJI[insight.insightType] || "\u2728";

  return (
    <button
      onClick={onRead}
      className="w-full text-start px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 flex gap-3"
    >
      <span className="text-base select-none flex-shrink-0 mt-0.5">{emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">
            {insight.title}
          </p>
          {isUnread && (
            <div className="w-2 h-2 rounded-full bg-ai animate-pulse flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
          {insight.message}
        </p>
        <span className="text-[10px] text-gray-400 mt-1 block">
          {timeAgo(insight.generatedAt, t)}
        </span>
      </div>
    </button>
  );
};
